<template>
  <div class="bbm-home">

    <!-- Hero Section -->
    <section class="bbm-hero">
      <video class="bbm-hero__video" autoplay muted loop playsinline>
        <source src="/images/static-home-page/brussels/beci-video.mp4" type="video/mp4"/>
      </video>
      <div class="bbm-hero__overlay"></div>
      <div class="bbm-hero__content">
        <div class="bbm-hero__text">
          <h1 class="bbm-hero__title">Brussels Innovation Ecosystem</h1>
          <p class="bbm-hero__subtitle">
            Discover companies, partners and opportunities in the Brussels business ecosystem.
            The definitive real-time map for innovation and growth in the heart of Europe.
          </p>
          <div class="tw-flex flex-wrap gap-4">
            <button class="bbm-btn-primary" @click="$router.push('/directory-brussels')">
              Explore the ecosystem
            </button>
            <button class="bbm-btn-outline" @click="$store.getters.isLoggedIn ? $store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR) : $router.push('/register')">
              + ADD MY ORGANISATION
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- User Needs Section -->
    <section class="bbm-section">
      <div class="bbm-container">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="need in userNeeds"
            :key="need.title"
            class="bbm-card bbm-card--needs group"
            @click="navigateTo(need)"
          >
            <span class="bbm-card__icon material-symbols-outlined">{{ need.icon }}</span>
            <h3 class="bbm-card__title">{{ need.title }}</h3>
            <p class="bbm-card__body">{{ need.description }}</p>
            <span class="bbm-card__action">
              {{ need.cta }}
              <span class="material-symbols-outlined bbm-card__arrow">arrow_forward</span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Expertise Pillars Section -->
    <section class="bbm-section bbm-section--tinted">
      <div class="bbm-container">
        <div class="bbm-section__header">
          <span class="bbm-label-caps">Specializations</span>
          <h2 class="bbm-section__title">Find experts in Brussels</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="pillar in expertisePillars"
            :key="pillar.key"
            class="bbm-card bbm-card--pillar"
            style="cursor: pointer;"
            @click="$store.getters.isLoggedIn ? $router.push('/spotting-areas/' + pillar.spottingAreaId) : $router.push({ path: '/directory-brussels', query: { pillar: pillar.key } })"
          >
            <span class="bbm-card__icon bbm-card__icon--sm material-symbols-outlined">{{ pillar.icon }}</span>
            <h4 class="bbm-card__title">{{ pillar.title }}</h4>
            <p class="bbm-card__body">{{ pillar.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured / Insights Section -->
    <!--    <section class="bbm-section bbm-section&#45;&#45;white">
          <div class="bbm-container">
            <div class="tw-flex justify-between items-end" style="margin-bottom: 48px;">
              <div>
                <span class="bbm-label-caps">Insights</span>
                <h2 class="bbm-section__title">What's happening in Brussels</h2>
              </div>
              <a class="bbm-link" @click="$router.push('/insights-brussels')" style="cursor:pointer;">View all events</a>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div v-for="card in featuredCards" :key="card.title" class="bbm-featured-card group">
                <div class="bbm-featured-card__image">
                  <img :src="card.image" :alt="card.title"/>
                </div>
                <div class="bbm-featured-card__body">
                  <span class="bbm-featured-card__badge" :style="{ background: card.badgeColor, color: card.badgeText }">{{ card.badge }}</span>
                  <h4 class="bbm-featured-card__title">{{ card.title }}</h4>
                  <p class="bbm-featured-card__meta">{{ card.meta }}</p>
                  <p class="bbm-featured-card__desc">{{ card.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>-->

    <!-- Map Section -->
    <section class="bbm-section bbm-section--map">
      <div class="bbm-container">
        <div class="bbm-map-header">
          <h2 class="bbm-section__title">Explore the ecosystem</h2>
          <p class="bbm-map-header__sub">
            Our interactive map allows you to filter the city by industry, stage, and neighbourhood to find the exact contacts you need.
          </p>
        </div>
      </div>
      <div class="bbm-map-wrapper">
        <iframe
          class="bbm-map-frame"
          src="https://brussels.datascouts.eu/ecosystem-maps/8/embed?apiKey=ds_live_VFyoesIWR0eSNrSez2aE"
          title="Brussels Ecosystem Map"
        ></iframe>
      </div>
    </section>

    <!-- Community / CTA Section -->
    <section class="bbm-community">
      <div class="bbm-community__bg-icon material-symbols-outlined">diversity_3</div>
      <div class="bbm-container" style="position: relative; z-index: 1;">
        <h2 class="bbm-community__title">Be part of the ecosystem</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bbm-community-card">
            <h3 class="bbm-community-card__title">HUB</h3>
            <p class="bbm-community-card__body">Connect and activate the ecosystem.</p>
            <p class="bbm-community-card__sub">For incubators, clusters, coworking spaces, universities, and ecosystem builders.</p>
            <button class="bbm-community-card__btn bbm-community-card__btn--accent" @click="$router.push('/register')">Become a Hub</button>
          </div>
          <div class="bbm-community-card">
            <h3 class="bbm-community-card__title">PARTICIPANT</h3>
            <p class="bbm-community-card__body">Represent the ecosystem.</p>
            <p class="bbm-community-card__sub">For startups, SMEs, investors, nonprofits, and organisations shaping Brussels.</p>
            <button class="bbm-community-card__btn bbm-community-card__btn--outline" @click="$router.push('/register')">Join the Ecosystem</button>
          </div>
          <div class="bbm-community-card">
            <h3 class="bbm-community-card__title">PARTNER</h3>
            <p class="bbm-community-card__body">Support the connected capital of Europe.</p>
            <p class="bbm-community-card__sub">For organisations committed to strengthening the visibility and connectivity of the Brussels ecosystem.</p>
            <button class="bbm-community-card__btn bbm-community-card__btn--outline" @click="$router.push('/register')">Become a Partner</button>
          </div>
        </div>
      </div>
    </section>

    <SimplifiedFooterBrussels/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'
  import { trackHeapEvent } from '@/util/analytics'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'

  export default defineComponent({
    name: 'StaticHomeBrusselsNew',
    computed: {
      MODAL_IDS() {
        return MODAL_IDS
      }, UI_MUTATION_TYPES() {
        return UI_MUTATION_TYPES
      }
    },
    components: {
      SimplifiedFooterBrussels,
    },
    data() {
      return {
        userNeeds: [
          {
            icon: 'rocket_launch',
            title: 'Start your company.',
            description: 'From the initial idea to start-up, we\'re here to help you launch your business in Brussels.',
            cta: 'Get started',
            pillar: 'start',
          },
          {
            icon: 'handshake',
            title: 'Expand your business.',
            description: 'Strategy, financing, location - we\'re there every step of the way',
            cta: 'Explore',
            pillar: 'expand',
          },
          {
            icon: 'bolt',
            title: "Grow your business abroad.",
            description: 'Go international and develop partnerships or exports.',
            cta: 'Go global',
            pillar: 'abroad',
          },
          {
            icon: 'person_search',
            title: 'Transform your business.',
            description: 'More sustainable, more innovative and more digital, step by step.',
            cta: 'Find expert',
            pillar: 'transform',
          },
        ],
        expertisePillars: [
          {
            key: 'business-support',
            icon: 'support',
            title: 'Business Support',
            description: 'Who can support my business journey in Brussels?',
            spottingAreaId: 531,
          },
          {
            key: 'consulting',
            icon: 'psychology',
            title: 'Consulting & Expertise',
            description: 'Who can support my project with specific expertise?',
            spottingAreaId: 532,
          },
          {
            key: 'entrepreneurship',
            icon: 'lightbulb',
            title: 'Entrepreneurship & Innovation',
            description: 'Where can I innovate, grow and launch a business in Brussels?',
            spottingAreaId: 530,
          },
          {
            key: 'talent',
            icon: 'groups',
            title: 'Talent & Human Capital',
            description: 'Who can help me recruit, manage or train my team in Brussels?',
            spottingAreaId: 528,
          },
          {
            key: 'visibility',
            icon: 'campaign',
            title: 'Network & Representation',
            description: 'How can I expand my network or represent my company in Brussels?',
            spottingAreaId: 527,
          },
          {
            key: 'workspace',
            icon: 'business_center',
            title: 'Working space in Brussels',
            description: 'Where can I work or host an event in Brussels?',
            spottingAreaId: 529,
          },
        ],
        featuredCards: [
          {
            badge: 'EVENT',
            badgeColor: 'var(--primary-community)',
            badgeText: '#799dd6',
            title: 'Tech Transfer & IP Summit',
            meta: 'Nov 12 · The Egg, Brussels',
            description: 'Join industry leaders discussing the future of intellectual property in the European Union.',
            image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCa8Xrat7FlnLvE5EWbpSpRlFoaxzo041hm2r8KtGdfaC9blkIxa09znujR5W4GmwnzAPgWu6ObhCKm3xLuqBLQsZ8d45nHMF2L9sDj4BwHNXJfl221A84APc2G73fjZb1sBBSeaVe8ra7MkS-D3wovbG8JxETz3-9MXUI3Fq_RiGwT6nZaDrtuI2M_Jj40yVTMcfgKf0KVFz9hil0-EZvxyIV96oQn4cb33WPWFB9Y4FnYexoNs5zuTXXhpQTJhjCmCEktxf9UMQ',
          },
          {
            badge: 'FEATURED ORG',
            badgeColor: '#00ccf9',
            badgeText: '#005266',
            title: 'Skyline AI',
            meta: 'Deep Tech · Brussels District',
            description: 'Revolutionizing real estate analytics through proprietary machine learning algorithms.',
            image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuB5U5-YRKCZBDErZAMxUrZWjGZwkrtECC9IvT6csjrMumtvhU_JpiqOvvCvZXsFFLrV6AVBmqYuobkF04KhQdEho36Mqt89N6jCehj7GQeeFJ9qcgImEKXqexswIfWz9D2nSQ0my23TxZbs-0Mm4myQ5yZSL3RGRHM6u3MbA7WU9UR8UrLafTB7WLSWc-ecjzkIBbbOtZQc9Gn5iIxPc4g9jnyQpcNbIfJR9_y-ppjYCu6pnVeFx8dfk3wW5AA6OngC490yg9R_0Q',
          },
          {
            badge: 'REPORT',
            badgeColor: '#802a00',
            badgeText: '#ffffff',
            title: 'Q3 Investment Report',
            meta: 'Ecosystem Intelligence',
            description: 'Analyzing the €450M venture capital flow into Brussels-based startups this quarter.',
            image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAQF2hgpzbsWbgRb5D8WdJifWEHs3IrJ4CEEGMTjWNcWg1MZPF4sCC0u_9E6CrACZdTSgy37Hzz0jKUn4BOs8BDWXnrWdWQdFF7uXlg3-uKp3RElOa9OD5p2WsBiZtOWLkKBtbr9veSqBxZoFjfRXW4Y-46QnTphZwuwQzG9wjOmU5trnW52qOmqjeENNJNoOa8oy81Zl9bUiwnQEMwcvr5iFTbI66sNAZL5jxIfzGK3VgMBnPgL-w',
          },
        ],
      }
    },
    methods: {
      trackHeapEvent,
      navigateTo(need: { pillar: string }) {
        this.$router.push({ path: '/journeys-brussels', query: { pillar: need.pillar } })
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-accent: #ff6f31;
  $bbm-primary-container: var(--primary-community);
  $bbm-muted: #43474f;
  $bbm-surface: #f7f9fb;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-home {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    overflow-x: hidden;
  }

  // Hero
  .bbm-hero {
    position: relative;
    height: 640px;
    display: flex;
    align-items: center;
    overflow: hidden;
    background: $bbm-primary;

  }

  .bbm-hero__video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
  }

  .bbm-hero__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, rgba(0, 30, 64, 0.95), rgba(0, 30, 64, 0.4));
    z-index: 1;
  }

  .bbm-hero__content {
    position: relative;
    z-index: 1;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
    width: 100%;
  }

  .bbm-hero__text {
    max-width: 680px;
  }

  .bbm-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: white;
    margin: 0 0 24px;
    line-height: 1.1;
  }

  .bbm-hero__subtitle {
    font-size: 1.125rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 40px;
  }

  .bbm-btn-primary {
    padding: 16px 32px;
    background: $bbm-accent;
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.04);
    }
  }

  .bbm-btn-outline {
    padding: 16px 32px;
    background: transparent;
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    border: 2px solid white;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  // Sections
  .bbm-section {
    padding: 80px 0;
  }

  .bbm-section--tinted {
    background: #f2f4f6;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
  }

  .bbm-section--white {
    background: white;
  }

  .bbm-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .bbm-section__header {
    margin-bottom: 48px;
  }

  .bbm-section__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 600;
    color: $bbm-primary;
    margin: 4px 0 0;
    line-height: 1.2;
  }

  .bbm-label-caps {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $bbm-accent;
  }

  // Cards - needs
  .bbm-card {
    background: white;
    border: 1px solid #e2e8f0;
    transition: box-shadow 0.25s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.12);
    }
  }

  .bbm-card--needs {
    padding: 32px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .bbm-card__icon {
    color: $bbm-accent;
    font-size: 2.5rem;
    margin-bottom: 24px;

    &.bbm-card__icon--sm {
      font-size: 1.75rem;
      margin-bottom: 16px;
    }
  }

  .bbm-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0 0 16px;
    line-height: 1.3;
  }

  .bbm-card__body {
    font-size: 1rem;
    line-height: 1.5;
    color: $bbm-muted;
    margin: 0 0 32px;
    flex-grow: 1;
  }

  .bbm-card__action {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $bbm-accent;
    transition: gap 0.2s;

    .group:hover & {
      gap: 16px;
    }
  }

  .bbm-card__arrow {
    font-size: 0.875rem;
  }

  // Pillar cards
  .bbm-card--pillar {
    padding: 32px;
    display: flex;
    flex-direction: column;
    border-transition: border-color 0.2s;

    &:hover {
      border-color: $bbm-accent;
      box-shadow: none;
    }
  }

  // Link
  .bbm-link {
    font-size: 1rem;
    color: $bbm-primary;
    border-bottom: 1px solid $bbm-primary;
    text-decoration: none;
    transition: color 0.15s, border-color 0.15s;

    &:hover {
      color: $bbm-accent;
      border-color: $bbm-accent;
    }
  }

  // Featured cards
  .bbm-featured-card {
    background: white;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    transition: border-color 0.2s;

    &:hover {
      border-color: $bbm-primary-container;
    }
  }

  .bbm-featured-card__image {
    height: 192px;
    overflow: hidden;
    background: #f1f5f9;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;

      .group:hover & {
        transform: scale(1.08);
      }
    }
  }

  .bbm-featured-card__body {
    padding: 24px;
  }

  .bbm-featured-card__badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 2px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .bbm-featured-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0 0 8px;
    line-height: 1.3;
  }

  .bbm-featured-card__meta {
    font-size: 0.9rem;
    color: $bbm-muted;
    margin: 0 0 12px;
  }

  .bbm-featured-card__desc {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }

  // Map
  .bbm-map-header {
    text-align: center;
    margin-bottom: 64px;

    .bbm-section__title {
      margin-bottom: 16px;
    }
  }

  .bbm-map-header__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: $bbm-muted;
    max-width: 600px;
    margin: 0 auto;
  }

  .bbm-section--map {
    padding-bottom: 0;
  }

  .bbm-map-wrapper {
    overflow: hidden;
    border-top: 1px solid #e2e8f0;
  }

  .bbm-map-frame {
    width: 100%;
    height: 600px;
    border: none;
    display: block;

    @media (min-width: 1024px) {
      height: 800px;
    }
  }

  // Community
  .bbm-community {
    background: $bbm-primary;
    color: white;
    padding: 96px 0;
    overflow: hidden;
    position: relative;
  }

  .bbm-community__bg-icon {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 400px;
    opacity: 0.08;
    line-height: 1;
    transform: translateX(30%);
    pointer-events: none;
    color: white;
  }

  .bbm-community__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 600;
    color: white;
    margin: 0 0 64px;
    line-height: 1.2;
  }

  .bbm-community-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    padding: 32px;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .bbm-community-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: white;
    margin: 0 0 16px;
  }

  .bbm-community-card__body {
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    margin: 0 0 10px;
  }

  .bbm-community-card__sub {
    font-size: 0.8rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.6);
    margin: 0 0 28px;
  }

  .bbm-community-card__btn {
    width: 100%;
    padding: 12px;
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    transition: filter 0.2s, background 0.2s, color 0.2s;

    &--accent {
      background: $bbm-accent;
      color: white;

      &:hover {
        filter: brightness(1.1);
      }
    }

    &--outline {
      background: transparent;
      color: white;
      border: 1px solid white;

      &:hover {
        background: white;
        color: $bbm-primary;
      }
    }
  }

  @media (max-width: 768px) {
    .bbm-hero {
      height: auto;
      padding: 80px 0 64px;
    }

    .bbm-hero__text {
      max-width: 100%;
    }
  }
</style>
